import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { useMsal } from "@azure/msal-react";
import { Worksheet, DayView, Container, PlanDataDay, Notification, ApiError } from "../types/types";
import { getStoreCodes, getAllStoreCodes, resolveDeviceGroups } from "../api/apiCallsCbd";
import { getKitchenSettings } from "../api/apiCallsKitchenSettings";
import { getPlanData } from "../api/apiCallsPlanData";
import { BusinessUnit } from "../types/types";
import { generateNextDays } from "../utils/dateUtils";
import { getStoreSelectionCookie, setStoreSelectionCookie } from "../utils/storeSelectionCookie";
import { useSignalR, SignalRHandlers, SignalRNotification } from "../signalr/useSignalR";


type DataState = {
  selectedWorksheet: Worksheet | null;
  worksheets: Worksheet[];
  selectedContainer: Container | null;
  containers: Container[];
  defaultDayViews: DayView[];
  dayViews: DayView[];
  selectedDayView: DayView | null;
  buCode: string;
  buType: string;
  marketCode: string;
  storeOptions: BusinessUnit[];
  storeSelectionRequired: boolean;
  planData: PlanDataDay[];
  selectedDate: Date;
  worksheetVersion: number;
  planDataVersion: number;
  storeVersion: number;
  notifications: Notification[];
  hasUnseenNotifications: boolean;
  apiError: ApiError | null;
};

type DataAction =
  | { type: "SET_SELECTED_WORKSHEET"; payload: Worksheet | null }
  | { type: "SET_WORKSHEETS"; payload: Worksheet[] }
  | { type: "SET_SELECTED_CONTAINER"; payload: Container | null }
  | { type: "SET_CONTAINERS"; payload: Container[] }
  | { type: "SET_TASKS"; payload: unknown[] }
  | { type: "SET_DEFAULT_DAYVIEWS"; payload: DayView[] }
  | { type: "SET_DAYVIEWS"; payload: DayView[] }
  | { type: "SET_SELECTED_DAYVIEW"; payload: DayView | null }
  | { type: "SET_BU_CODE"; payload: string }
  | { type: "SET_BU_TYPE"; payload: string }
  | { type: "SET_MARKET_CODE"; payload: string }
  | { type: "SET_STORE_OPTIONS"; payload: BusinessUnit[] }
  | { type: "SET_STORE_SELECTION_REQUIRED"; payload: boolean }
  | { type: "SET_PLAN_DATA"; payload: PlanDataDay[] }
  | { type: "SET_SELECTED_DATE"; payload: Date }
  | { type: "INCREMENT_WORKSHEET_VERSION" }
  | { type: "INCREMENT_PLAN_DATA_VERSION" }
  | { type: "INCREMENT_STORE_VERSION" }
  | { type: "UPDATE_PLAN_ITEM"; payload: { plannedDayHour: string; itemCode: string; producedVol: number; availableVol: number; statusCode: string } }
  | { type: "ADD_NOTIFICATION"; payload: Notification }
  | { type: "CLEAR_NOTIFICATIONS" }
  | { type: "MARK_NOTIFICATIONS_SEEN" }
  | { type: "SET_API_ERROR"; payload: ApiError }
  | { type: "CLEAR_API_ERROR" };


export type DataDispatch = React.Dispatch<DataAction>;


const initialState: DataState = {
  selectedWorksheet: null,
  worksheets: [],
  selectedContainer: null,
  containers: [],
  defaultDayViews: [],
  dayViews: [],
  buCode: "",
  buType: "",
  marketCode: "",
  storeOptions: [],
  storeSelectionRequired: false,
  selectedDayView: null,
  planData: [],
  selectedDate: new Date(),
  worksheetVersion: 0,
  planDataVersion: 0,
  storeVersion: 0,
  notifications: [],
  hasUnseenNotifications: false,
  apiError: null,
};

function dataReducer(state: DataState, action: DataAction): DataState {
  switch (action.type) {
    case "SET_SELECTED_WORKSHEET":
      return { ...state, selectedWorksheet: action.payload };
    case "SET_WORKSHEETS":
      return { ...state, worksheets: action.payload };
    case "SET_SELECTED_CONTAINER":
      return { ...state, selectedContainer: action.payload };
    case "SET_CONTAINERS":
      return { ...state, containers: action.payload };
    case "SET_DEFAULT_DAYVIEWS":
      return { ...state, defaultDayViews: action.payload };
    case "SET_DAYVIEWS":
      return { ...state, dayViews: action.payload };
    case "SET_SELECTED_DAYVIEW":
      return { ...state, selectedDayView: action.payload };
    case "SET_BU_CODE":
      return { ...state, buCode: action.payload };
    case "SET_BU_TYPE":
      return { ...state, buType: action.payload };
    case "SET_MARKET_CODE":
      return { ...state, marketCode: action.payload };
    case "SET_STORE_OPTIONS":
      return { ...state, storeOptions: action.payload };
    case "SET_STORE_SELECTION_REQUIRED":
      return { ...state, storeSelectionRequired: action.payload };
    case "SET_PLAN_DATA":
      return { ...state, planData: action.payload };
    case "SET_SELECTED_DATE":
      return { ...state, selectedDate: action.payload };
    case "INCREMENT_WORKSHEET_VERSION":
      return { ...state, worksheetVersion: state.worksheetVersion + 1 };
    case "INCREMENT_PLAN_DATA_VERSION":
      return { ...state, planDataVersion: state.planDataVersion + 1 };
    case "INCREMENT_STORE_VERSION":
      return { ...state, storeVersion: state.storeVersion + 1 };
    case "ADD_NOTIFICATION":
      return { ...state, notifications: [action.payload, ...state.notifications], hasUnseenNotifications: true };
    case "CLEAR_NOTIFICATIONS":
      return { ...state, notifications: [], hasUnseenNotifications: false };
    case "MARK_NOTIFICATIONS_SEEN":
      return { ...state, hasUnseenNotifications: false };
    case "SET_API_ERROR":
      return { ...state, apiError: action.payload };
    case "CLEAR_API_ERROR":
      return { ...state, apiError: null };
    case "UPDATE_PLAN_ITEM": {
      const { plannedDayHour, itemCode, producedVol, availableVol, statusCode } = action.payload;
      const planDay = plannedDayHour.substring(0, 8);
      
      return {
        ...state,
        planData: state.planData.map((day) => {
          if (day.planDay !== planDay) return day;
          
          return {
            ...day,
            dayPlanDatas: day.dayPlanDatas.map((planItem) => {
              if (planItem.itemCode !== itemCode) return planItem;
              
              return {
                ...planItem,
                itemSalesPlanDtos: planItem.itemSalesPlanDtos.map((dto) => {
                  if (dto.plannedDayHour !== plannedDayHour) return dto;
                  
                  return {
                    ...dto,
                    producedVol,
                    availableVol,
                    statusCode,
                  };
                }),
              };
            }),
          };
        }),
      };
    }
    default:
      return state;
  }
}

interface IDataContext extends DataState {
  dispatch: React.Dispatch<DataAction>;
  selectStore: (buCode: string, buType: string) => void;
  isDeviceApp: boolean;
}

const DataContext = createContext<IDataContext>({
  ...initialState,
  dispatch: () => {},
  selectStore: () => {},
  isDeviceApp: false,
});

export function DataContextProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const buCodeRef = useRef(state.buCode);
  const { accounts } = useMsal();
  const user = accounts[0];

  const ctry = (user?.idTokenClaims as { ctry?: string })?.ctry || "";
  const userRoles = (user?.idTokenClaims as { roles?: string[] })?.roles || [];
  const userGroups = (user?.idTokenClaims as { groups?: string[] })?.groups || [];
  const isSystemAdmin = userRoles.some((role) => role.toLowerCase() === "system.administrator");
  const isDeviceApp = userRoles.some((role) => role.toLowerCase() === "device.app.access");

  useEffect(() => {
    buCodeRef.current = state.buCode;
  }, [state.buCode]);

  const selectStore = (buCode: string, buType: string) => {
    dispatch({ type: "SET_BU_CODE", payload: buCode });
    dispatch({ type: "SET_BU_TYPE", payload: buType });
    dispatch({ type: "SET_STORE_SELECTION_REQUIRED", payload: false });
    dispatch({ type: "INCREMENT_STORE_VERSION" });
    if (state.marketCode) {
      setStoreSelectionCookie(state.marketCode, buCode, buType);
    }
  };

  // Store initialization: read cookie or require selection
  useEffect(() => {
    if (!user) return;

    if (isDeviceApp) {
      dispatch({ type: "SET_MARKET_CODE", payload: "Device.App.Access" });
      resolveDeviceGroups(userGroups)
        .then((group) => {
          if (!group) {
            console.error("Unable to resolve store for device.app.access role: no matching device group found");
            dispatch({ type: "SET_STORE_SELECTION_REQUIRED", payload: true });
            return;
          }
          dispatch({ type: "SET_BU_CODE", payload: group.buCode });
          dispatch({ type: "SET_BU_TYPE", payload: group.buType });
          dispatch({ type: "SET_STORE_SELECTION_REQUIRED", payload: false });
        })
        .catch((error) => {
          console.error("Error resolving store for device.app.access role:", error);
          dispatch({ type: "SET_STORE_SELECTION_REQUIRED", payload: true });
        });
      return;
    }

    const cookieKey = isSystemAdmin ? "System.Administrator" : ctry;
    if (!cookieKey) return;
    dispatch({ type: "SET_MARKET_CODE", payload: cookieKey });
    const savedStore = getStoreSelectionCookie(cookieKey);
    if (savedStore) {
      dispatch({ type: "SET_BU_CODE", payload: savedStore.buCode });
      dispatch({ type: "SET_BU_TYPE", payload: savedStore.buType });
      dispatch({ type: "SET_STORE_SELECTION_REQUIRED", payload: false });
    } else {
      dispatch({ type: "SET_STORE_SELECTION_REQUIRED", payload: true });
    }
    if (isSystemAdmin) {
      getAllStoreCodes().then((units) => {
        dispatch({ type: "SET_STORE_OPTIONS", payload: units });
      });
    } else {
      getStoreCodes(ctry).then((units) => {
        dispatch({ type: "SET_STORE_OPTIONS", payload: units });
      });
    }
  }, [ctry, isSystemAdmin, isDeviceApp]);


  // Maps each SignalR topic to the specific reducer action that triggers
  // only the affected endpoint to be re-fetched.
  const TOPIC_TO_REFRESH_ACTION: Record<string, DataAction["type"]> = {
    WORKSHEET_ADDED: "INCREMENT_WORKSHEET_VERSION",
    WORKSHEET_UPDATED: "INCREMENT_WORKSHEET_VERSION",
    WORKSHEET_DELETED: "INCREMENT_WORKSHEET_VERSION",
    TASK_INPUT_ADDED: "INCREMENT_PLAN_DATA_VERSION",
    TASK_INPUT_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_SALES_PLAN_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_WORKSHEET_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_TASK_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_PRIO_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_PPSI_URL_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_ADDITIONAL_INFO_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_TASK_INPUT_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
    ITEM_CONTAINER_CONVERSION_UPDATED: "INCREMENT_PLAN_DATA_VERSION",
  };

  const handleKitchenSettingsNotification = (notification: SignalRNotification) => {
    const { topic, entityId, entityName, buCode, buType } = notification;

    if (buCode !== buCodeRef.current) {
      console.warn(
        `[SignalR] Ignoring notification for different buCode. Current: ${buCodeRef.current}, Received: ${buCode}`,
      );
      return;
    }

    const refreshAction = TOPIC_TO_REFRESH_ACTION[topic];
    if (!refreshAction) {
      console.warn(`[SignalR] No refresh mapping for topic: ${topic}`);
      return;
    }

    console.log(
      `%c[SignalR] Kitchen settings update: ${topic} (entityId: ${entityId}, entityName: ${entityName}, buCode: ${buCode}, buType: ${buType})`,
      "color: #0b7a6f; font-weight: 700;",
    );

    dispatch({ type: refreshAction } as DataAction);

    dispatch({
      type: "ADD_NOTIFICATION",
      payload: {
        id: `${topic}-${entityId}-${Date.now()}`,
        topic,
        entityId,
        entityName,
        buCode,
        timestamp: new Date(),
      },
    });
  };

  const kitchenSettingsSignalRHandlers: SignalRHandlers = {
    WORKSHEET_ADDED: handleKitchenSettingsNotification,
    WORKSHEET_UPDATED: handleKitchenSettingsNotification,
    WORKSHEET_DELETED: handleKitchenSettingsNotification,
    TASK_ADDED: handleKitchenSettingsNotification,
    TASK_UPDATED: handleKitchenSettingsNotification,
    TASK_DELETED: handleKitchenSettingsNotification,
    TASK_INPUT_ADDED: handleKitchenSettingsNotification,
    TASK_INPUT_UPDATED: handleKitchenSettingsNotification,
    DISH_UPDATED: handleKitchenSettingsNotification,
    ITEM_SALES_PLAN_UPDATED: handleKitchenSettingsNotification,
    ITEM_WORKSHEET_UPDATED: handleKitchenSettingsNotification,
    ITEM_TASK_UPDATED: handleKitchenSettingsNotification,
    ITEM_PRIO_UPDATED: handleKitchenSettingsNotification,
    ITEM_PPSI_URL_UPDATED: handleKitchenSettingsNotification,
    ITEM_ADDITIONAL_INFO_UPDATED: handleKitchenSettingsNotification,
    ITEM_TASK_INPUT_UPDATED: handleKitchenSettingsNotification,
    ITEM_CONTAINER_CONVERSION_UPDATED: handleKitchenSettingsNotification,
  };

  useSignalR(buCodeRef, "COWORKER", kitchenSettingsSignalRHandlers);

  // Initial load and BU change: fetch all kitchen settings
  useEffect(() => {
    if (!state.buCode) return;
    ["day-view-defaults", "dayview"].forEach((endpoint) => {
      getKitchenSettings({ endpoint, buType: state.buType, buCode: state.buCode, callBack: dispatch });
    });
  }, [state.buCode, state.buType, state.storeVersion]);

  useEffect(() => {
    if (!state.buCode) return;
    getKitchenSettings({ endpoint: "worksheet", buType: state.buType, buCode: state.buCode, callBack: dispatch });
  }, [state.buCode, state.buType, state.worksheetVersion, state.storeVersion]);

  useEffect(() => {
    if (!state.buCode) return;
    ["task", "task-inputs", "container"].forEach((endpoint) => {
      getKitchenSettings({ endpoint, buType: state.buType, buCode: state.buCode, callBack: dispatch });
    });
  }, [state.buCode, state.buType, state.storeVersion]);

  useEffect(() => {
    if (!state.buCode) return;
    const days = generateNextDays(7);
    getPlanData({
      days: days,
      buType: state.buType,
      buCode: state.buCode,
      callBack: dispatch,
    });
  }, [state.buCode, state.buType, state.planDataVersion, state.storeVersion]);

  return (
    <DataContext.Provider value={{ ...state, dispatch, selectStore, isDeviceApp }}>
      {children}
    </DataContext.Provider>
  );
}

export function useDataContext() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within a DataContextProvider");
  }
  return context;
}
